// CRM annotations and organization relationships (spec §4, §6; ADR 0005).
// The vocabularies live here so core, the web Worker and the UI agree on
// them, and adding a word is one reviewed change.

export const NOTE_MAX_LENGTH = 10_000

// What a note can be about. A facility or a space joined in ADR 0013; its
// notes follow facilities.read and facilities.manage instead of the
// people capabilities.
export const NOTE_SUBJECT_TYPES = ['person', 'organization', 'facility', 'space'] as const
export type NoteSubjectType = (typeof NOTE_SUBJECT_TYPES)[number]
export type NoteVisibility = 'internal' | 'confidential'

export type Note = {
  id: string
  subjectType: NoteSubjectType
  subjectId: string
  visibility: NoteVisibility
  body: string
  pinned: boolean
  version: number
  edited: boolean
  authorPersonId: string | null
  authorName: string | null
  createdAt: string
  updatedAt: string
  editedAt: string | null
  editedByName: string | null
  retiredAt: string | null
  retiredByName: string | null
  retireReason: string | null
}

export type NoteVersion = {
  version: number
  body: string
  visibility: NoteVisibility
  writtenByPersonId: string | null
  writtenByName: string | null
  writtenAt: string
}

// What an organization plays for BRE. One organization may hold several
// at once (spec §4).
export const ORGANIZATION_ROLES = {
  sponsor: 'Sponsor',
  renter: 'Renter',
  vendor: 'Vendor',
  funder: 'Funder',
  promoter: 'Promoter',
  group_buyer: 'Group buyer',
  // Pays BRE for a booth at a show (Craft Show; ADR 0021). Not a vendor:
  // BRE buys from vendors.
  exhibitor: 'Exhibitor',
} as const

// Organization-to-organization links. Stored once, read from both sides:
// `label` from the organization the link starts at, `inverse` from the
// other one.
export const ORGANIZATION_LINKS = {
  parent_of: { label: 'Parent of', inverse: 'Subsidiary of' },
  affiliate_of: { label: 'Affiliate of', inverse: 'Affiliate of' },
  venue_partner_of: { label: 'Venue partner of', inverse: 'Venue partner of' },
} as const

export type OrganizationRole = keyof typeof ORGANIZATION_ROLES
export type OrganizationLinkType = keyof typeof ORGANIZATION_LINKS
export type RelationshipType = OrganizationRole | OrganizationLinkType
export type RelationshipStatus = 'proposed' | 'confirmed' | 'rejected' | 'ended'

export const ORGANIZATION_ROLE_TYPES = Object.keys(ORGANIZATION_ROLES) as OrganizationRole[]
export const ORGANIZATION_LINK_TYPES = Object.keys(ORGANIZATION_LINKS) as OrganizationLinkType[]
export const RELATIONSHIP_TYPES: RelationshipType[] = [...ORGANIZATION_ROLE_TYPES, ...ORGANIZATION_LINK_TYPES]

export const isOrganizationRole = (value: string): value is OrganizationRole => value in ORGANIZATION_ROLES
export const isOrganizationLinkType = (value: string): value is OrganizationLinkType => value in ORGANIZATION_LINKS

export type OrganizationRelationship = {
  id: string
  type: RelationshipType
  kind: 'role' | 'link'
  status: RelationshipStatus
  organizationId: string
  organizationName: string
  relatedOrganizationId: string | null
  relatedOrganizationName: string | null
  effectiveFrom: string | null
  effectiveTo: string | null
  note: string | null
  evidence: { id: string; sourceService: string; entityType: string; sourceId: string; environment: string } | null
  provenance: string
  proposedByPersonId: string | null
  proposedByName: string | null
  proposedAt: string
  decidedByName: string | null
  decidedAt: string | null
  selfConfirmed: boolean
  rejectReason: string | null
  endedByName: string | null
  endedAt: string | null
  endReason: string | null
}

// "Current" is confirmed and in effect on the given day (YYYY-MM-DD).
export function isCurrentRelationship(r: Pick<OrganizationRelationship, 'status' | 'effectiveFrom' | 'effectiveTo'>, today: string): boolean {
  return r.status === 'confirmed' && (r.effectiveFrom === null || r.effectiveFrom <= today) && (r.effectiveTo === null || r.effectiveTo >= today)
}

// How a relationship reads from one organization's page.
export function relationshipLabel(r: Pick<OrganizationRelationship, 'type' | 'organizationId'>, viewedFrom: string): string {
  if (isOrganizationRole(r.type)) return ORGANIZATION_ROLES[r.type]
  const link = ORGANIZATION_LINKS[r.type]
  return r.organizationId === viewedFrom ? link.label : link.inverse
}
